import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ArrowRight, Bot, Server, Handshake } from "lucide-react";

const menu = [
  {
    label: "AI Voice Agent",
    featured: { icon: Bot, title: "AI Voice Agent", desc: "A voice agent that answers, speaks and sells — built into the network." },
    children: [
      { label: "Live Agent Demos", to: "/ai/demos", desc: "Talk to agents built for 5 use cases" },
      { label: "Overview", to: "/ai/overview", desc: "The full AI agent suite" },
      { label: "Inbound AI Calls", to: "/ai/inbound", desc: "Answer every incoming call" },
      { label: "Outbound AI Calls", to: "/ai/outbound", desc: "Calls on your caller ID" },
      { label: "Web Chat & Widget", to: "/ai/web-chat", desc: "The agent on your site" },
      { label: "Multilingual", to: "/ai/multilingual", desc: "English · Hindi · Hinglish" },
      { label: "Call Analytics & Insights", to: "/ai/analytics", desc: "Sentiment, intent, topics" },
    ],
  },
  {
    label: "PBX Platform",
    featured: { icon: Server, title: "PBX Platform", desc: "A complete, carrier-grade phone system you fully control." },
    children: [
      { label: "Multi-Tenant PBX", to: "/pbx/multi-tenant", desc: "Isolated business accounts" },
      { label: "Features", to: "/pbx/features", desc: "Extensions, IVR, queues, DIDs" },
      { label: "Numbers & Coverage", to: "/numbers", desc: "India, USA & 150+ countries" },
      { label: "Softphone & Agent Portal", to: "/pbx/softphone", desc: "Call from any browser" },
      { label: "Security & Reliability", to: "/pbx/security", desc: "Whitelisting & RBAC" },
      { label: "Analytics & Reporting", to: "/pbx/analytics", desc: "CDRs, agents & KPIs" },
    ],
  },
  { label: "Pricing", to: "/pricing" },
  {
    label: "Partners",
    featured: { icon: Handshake, title: "Partners", desc: "Resell, white-label or refer OwnPBX under your own brand." },
    children: [
      { label: "Become a Reseller", to: "/partners/reseller", desc: "Sell under your brand" },
      { label: "White-Label Program", to: "/partners/white-label", desc: "Fully rebrand OwnPBX" },
      { label: "Partner / Affiliate", to: "/partners/affiliate", desc: "Refer and co-sell" },
    ],
  },
  { label: "Guides", to: "/guides" },
  { label: "Docs", to: "/docs" },
  { label: "About", to: "/about" },
  { label: "Careers", to: "/careers" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [mobileSub, setMobileSub] = useState(null);
  const [pastHero, setPastHero] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const solid = scrolled || !isHome;
  // Hero is dark and roughly one viewport tall — until scroll actually
  // clears it, the nav needs a dark-tinted backdrop and light text instead
  // of flipping to its normal light styling at the first few px of scroll.
  const onDarkHero = isHome && !pastHero;

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
      setPastHero(window.scrollY > window.innerHeight * 0.85);
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        !solid
          ? "border-b border-transparent"
          : onDarkHero
          ? "bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-white/10"
          : "bg-[#F7F6F2]/85 backdrop-blur-xl border-b border-black/10"
      }`}
      data-testid="main-nav"
      onMouseLeave={() => setHovered(null)}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group shrink-0" data-testid="nav-logo">
          <img
            src="/brand/ownpbx-mark.png"
            alt=""
            width="36"
            height="36"
            className="w-9 h-9 group-hover:rotate-[-8deg] transition-transform duration-300"
          />
          <span className={`font-heading font-medium text-lg tracking-tight ${onDarkHero ? "text-white" : "text-neutral-900"}`}>OwnPBX<sup className="text-[0.5em] align-super font-semibold ml-px">™</sup></span>
        </Link>

        <nav className="hidden xl:flex items-center gap-7">
          {menu.map((item) =>
            item.children ? (
              <div key={item.label} className="relative" onMouseEnter={() => setHovered(item.label)}>
                <button
                  className={`flex items-center gap-1 text-sm font-medium transition-colors ${onDarkHero ? "text-neutral-300 hover:text-white" : "text-neutral-600 hover:text-neutral-900"}`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                  <ChevronDown size={14} className={`transition-transform ${hovered === item.label ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {hovered === item.label && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-1/2 -translate-x-1/2 pt-4"
                    >
                      <div className="flex w-[560px] rounded-xl overflow-hidden border border-white/10 bg-[#0a0a0a] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)]">
                        {item.featured && (
                          <div className="w-[200px] shrink-0 bg-white/[0.03] border-r border-white/10 p-6 flex flex-col justify-center">
                            <item.featured.icon size={22} className="text-[#5B8CFF]" />
                            <p className="mt-4 text-sm font-semibold text-white">{item.featured.title}</p>
                            <p className="mt-1.5 text-xs text-neutral-400 leading-relaxed">{item.featured.desc}</p>
                          </div>
                        )}
                        <div className="flex-1 p-2">
                          {item.children.map((c) => (
                            <Link
                              key={c.to}
                              to={c.to}
                              className="group flex items-start gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors"
                              data-testid={`nav-item-${c.to.replace(/\//g, "-")}`}
                            >
                              <div className="flex-1">
                                <p className="text-sm font-semibold text-white">{c.label}</p>
                                {c.desc && <p className="text-xs text-neutral-500 mt-0.5">{c.desc}</p>}
                              </div>
                              <ArrowRight size={14} className="text-neutral-600 group-hover:text-white group-hover:translate-x-0.5 transition-all mt-0.5" />
                            </Link>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                key={item.label}
                to={item.to}
                onMouseEnter={() => setHovered(null)}
                className={`text-sm font-medium transition-colors ${onDarkHero ? "text-neutral-300 hover:text-white" : "text-neutral-600 hover:text-neutral-900"}`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden xl:flex items-center gap-3 shrink-0">
          <Link to="/demo" className={`text-sm font-medium px-4 py-2.5 transition-colors duration-300 ${onDarkHero ? "text-neutral-300 hover:text-white" : "text-neutral-600 hover:text-neutral-900"}`} data-testid="nav-demo">
            Book a Demo
          </Link>
          <Link to="/signup" className="text-sm font-bold bg-[#0047FF] text-white px-5 py-2.5 hover:bg-blue-700 transition-colors duration-300" data-testid="nav-cta">
            Get Started
          </Link>
        </div>

        <button className={`xl:hidden ${onDarkHero ? "text-white" : "text-neutral-900"}`} onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle" aria-label="Toggle menu">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="xl:hidden bg-[#F7F6F2] border-t border-black/10 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col max-h-[70vh] overflow-y-auto">
              {menu.map((item) =>
                item.children ? (
                  <div key={item.label} className="border-b border-black/10">
                    <button
                      className="w-full flex items-center justify-between py-3.5 font-semibold text-neutral-900"
                      onClick={() => setMobileSub(mobileSub === item.label ? null : item.label)}
                    >
                      {item.label}
                      <ChevronDown size={16} className={`transition-transform ${mobileSub === item.label ? "rotate-180" : ""}`} />
                    </button>
                    {mobileSub === item.label && (
                      <div className="pb-3 pl-3 flex flex-col gap-1">
                        {item.children.map((c) => (
                          <Link key={c.to} to={c.to} className="py-2 text-sm text-neutral-600">{c.label}</Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link key={item.label} to={item.to} className="py-3.5 font-semibold text-neutral-900 border-b border-black/10">
                    {item.label}
                  </Link>
                )
              )}
              <Link to="/demo" className="mt-5 border border-black/15 text-neutral-900 px-5 py-3.5 font-bold text-center hover:border-black/40 transition-colors">Book a Demo</Link>
              <Link to="/signup" className="mt-3 bg-[#0047FF] text-white px-5 py-3.5 font-bold text-center hover:bg-blue-700 transition-colors">Get Started</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
